/**
 * Prompt Editor Page (Admin)
 *
 * Admin interface for managing AI prompts with version control:
 * - Edit prompts for all 9 prompt types
 * - View version history (last 20+ versions)
 * - Restore previous versions
 * - Immutable version history (spec 4.2.2.9)
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - NO localStorage
 * - Prompt versions are IMMUTABLE (no updates/deletes)
 * - Restore creates NEW version with old content
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { adminFunctions } from '@/firebaseConfig';
import { useToast } from '@/contexts/ToastContext';
import { Button, Card, Modal, LoadingSkeleton } from '@/components/ui';
import { AdminHeader } from '@/components/admin/AdminHeader';
import {
  type PromptVersionItem,
  type SavePromptRequest,
  type SavePromptResponse,
  type GetPromptHistoryRequest,
  type GetPromptHistoryResponse,
  type GetActivePromptRequest,
  type GetActivePromptResponse,
  type RestorePromptRequest,
  type RestorePromptResponse,
} from '@/types/admin.types';
import { PromptType } from '@/shared/types';
import { formatDate } from '@/utils/formatting';

/**
 * Prompt category for UI organization
 */
type PromptCategory = 'IMAGE' | 'STAGING' | 'DESCRIPTION';

/**
 * Map PromptType to display name
 */
function getPromptDisplayName(promptType: PromptType): string {
  const names: Record<PromptType, string> = {
    [PromptType.DAY_TO_DUSK]: 'Day to Dusk',
    [PromptType.ITEM_REMOVAL]: 'Item Removal',
    [PromptType.STAGING_MODERN]: 'Modern Style',
    [PromptType.STAGING_MINIMALIST]: 'Minimalist Style',
    [PromptType.STAGING_CLASSIC]: 'Classic Style',
    [PromptType.STAGING_CUSTOM]: 'Custom Style',
    [PromptType.DESCRIPTION_CAPTION]: 'Caption',
    [PromptType.DESCRIPTION_DESCRIPTION]: 'Description',
    [PromptType.DESCRIPTION_MLS_REMARKS]: 'MLS Remarks',
    [PromptType.DESTAGING]: 'Virtual Destaging',
    [PromptType.COLLAGE]: 'AI Dollhouse View',
    [PromptType.WATERMARK]: 'Watermark System',
    [PromptType.ENHANCEMENT_GENERAL]: 'General Enhancement',
  };
  return names[promptType] || promptType;
}

/**
 * Get category for a prompt type
 */
function getPromptCategory(promptType: PromptType): PromptCategory {
  if (promptType.startsWith('STAGING_')) return 'STAGING';
  if (promptType.startsWith('DESCRIPTION_')) return 'DESCRIPTION';
  return 'IMAGE';
}

/**
 * Prompt types by category for UI organization
 */
const PROMPT_CATEGORIES: Record<PromptCategory, { label: string; types: PromptType[] }> = {
  IMAGE: {
    label: 'Image Services',
    types: [
      PromptType.DAY_TO_DUSK,
      PromptType.ITEM_REMOVAL,
      PromptType.DESTAGING,
      PromptType.COLLAGE,
      PromptType.WATERMARK,
      PromptType.ENHANCEMENT_GENERAL,
    ],
  },
  STAGING: {
    label: 'Virtual Staging',
    types: [
      PromptType.STAGING_MODERN,
      PromptType.STAGING_MINIMALIST,
      PromptType.STAGING_CLASSIC,
      PromptType.STAGING_CUSTOM,
    ],
  },
  DESCRIPTION: {
    label: 'Descriptions',
    types: [
      PromptType.DESCRIPTION_CAPTION,
      PromptType.DESCRIPTION_DESCRIPTION,
      PromptType.DESCRIPTION_MLS_REMARKS,
    ],
  },
};

export function PromptEditor(): React.JSX.Element {
  const { showToast } = useToast();

  // Selected prompt type
  const [selectedPromptType, setSelectedPromptType] = useState<PromptType>(
    PromptType.DAY_TO_DUSK
  );

  // Prompt content
  const [promptContent, setPromptContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Version history
  const [versions, setVersions] = useState<PromptVersionItem[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Restore modal
  const [restoreModal, setRestoreModal] = useState<{
    isOpen: boolean;
    version: PromptVersionItem | null;
  }>({ isOpen: false, version: null });

  /**
   * Load active prompt for selected type
   */
  const loadActivePrompt = async (): Promise<void> => {
    try {
      setLoading(true);

      const getActiveFunc = httpsCallable<
        GetActivePromptRequest,
        GetActivePromptResponse
      >(adminFunctions, 'getActivePrompt');

      const result = await getActiveFunc({
        promptType: selectedPromptType,
      });

      if (result.data.found && result.data.prompt) {
        setPromptContent(result.data.prompt.content);
      } else {
        setPromptContent('');
        showToast('No active prompt found for this type', 'info');
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading active prompt:', error);
      showToast('Failed to load active prompt', 'error');
      setLoading(false);
    }
  };

  /**
   * Load version history
   */
  const loadVersionHistory = async (): Promise<void> => {
    try {
      setLoadingVersions(true);

      const getHistoryFunc = httpsCallable<
        GetPromptHistoryRequest,
        GetPromptHistoryResponse
      >(adminFunctions, 'getPromptHistory');

      const result = await getHistoryFunc({
        promptType: selectedPromptType,
        limit: 20,
      });

      setVersions(result.data.versions);
      setLoadingVersions(false);
    } catch (error) {
      console.error('Error loading version history:', error);
      showToast('Failed to load version history', 'error');
      setLoadingVersions(false);
    }
  };

  /**
   * Load active prompt when prompt type changes
   */
  useEffect(() => {
    loadActivePrompt();
    loadVersionHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPromptType]);

  /**
   * Save new prompt version
   */
  const handleSave = async (): Promise<void> => {
    if (!promptContent.trim()) {
      showToast('Prompt content cannot be empty', 'error');
      return;
    }

    try {
      setSaving(true);

      const saveFunc = httpsCallable<SavePromptRequest, SavePromptResponse>(
        adminFunctions,
        'savePromptVersion'
      );

      await saveFunc({
        promptType: selectedPromptType,
        content: promptContent.trim(),
      });

      showToast('Prompt saved successfully', 'success');

      // Reload version history
      await loadVersionHistory();

      setSaving(false);
    } catch (error) {
      console.error('Error saving prompt:', error);
      showToast('Failed to save prompt', 'error');
      setSaving(false);
    }
  };

  /**
   * Handle restore version
   */
  const handleRestore = async (): Promise<void> => {
    if (!restoreModal.version) return;

    try {
      const restoreFunc = httpsCallable<RestorePromptRequest, RestorePromptResponse>(
        adminFunctions,
        'restorePrompt'
      );

      await restoreFunc({
        versionId: restoreModal.version.id,
      });

      showToast(
        `Version ${restoreModal.version.version} restored successfully`,
        'success'
      );

      // Reload active prompt and version history
      await loadActivePrompt();
      await loadVersionHistory();

      setRestoreModal({ isOpen: false, version: null });
    } catch (error) {
      console.error('Error restoring version:', error);
      showToast('Failed to restore version', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <AdminHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Prompt Editor</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage AI prompts with immutable version control
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor Section */}
          <div className="lg:col-span-2">
            <Card>
              <div className="p-6">
                {/* Category Sections */}
                <div className="space-y-4 mb-6">
                  {Object.entries(PROMPT_CATEGORIES).map(([category, { label, types }]) => (
                    <div key={category}>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {label}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {types.map((promptType) => (
                          <button
                            key={promptType}
                            onClick={() => setSelectedPromptType(promptType)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${selectedPromptType === promptType
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                          >
                            {getPromptDisplayName(promptType)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Current Selection Info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700">
                    <strong>Selected:</strong> {getPromptDisplayName(selectedPromptType)}
                    <span className="text-gray-500 ml-2">
                      ({getPromptCategory(selectedPromptType)})
                    </span>
                  </p>
                </div>

                {/* Prompt Textarea */}
                {loading ? (
                  <div className="space-y-3">
                    <LoadingSkeleton variant="text" width="w-full" height="h-4" />
                    <LoadingSkeleton variant="text" width="w-full" height="h-4" />
                    <LoadingSkeleton variant="text" width="w-3/4" height="h-4" />
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prompt Content
                      </label>
                      <textarea
                        value={promptContent}
                        onChange={(e) => setPromptContent(e.target.value)}
                        className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="Enter AI prompt content here..."
                      />
                      <div className="mt-2 text-sm text-gray-500 text-right">
                        {promptContent.length} characters
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                      <Button
                        variant="secondary"
                        onClick={() => setShowVersionHistory(!showVersionHistory)}
                      >
                        {showVersionHistory ? 'Hide' : 'Show'} Version History
                      </Button>
                      <Button onClick={handleSave} disabled={saving || !promptContent.trim()}>
                        {saving ? 'Saving...' : 'Save New Version'}
                      </Button>
                    </div>

                    {/* Info Box */}
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Version Control:</strong> Saving creates a new immutable
                        version. All previous versions are preserved. To rollback, use the
                        version history.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </div>

          {/* Version History Sidebar */}
          <div className="lg:col-span-1">
            {showVersionHistory && (
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Version History
                  </h3>

                  {loadingVersions ? (
                    <div className="space-y-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <LoadingSkeleton key={i} variant="text" width="w-full" height="h-20" />
                      ))}
                    </div>
                  ) : versions.length === 0 ? (
                    <p className="text-sm text-gray-500">No version history available</p>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {versions.map((version, index) => {
                        const isLatest = index === 0;
                        return (
                          <div
                            key={version.id}
                            className={`p-3 rounded-lg border ${isLatest
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 bg-white'
                              }`}
                          >
                            {/* Version Header */}
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <span className="text-sm font-semibold text-gray-900">
                                  Version {version.version}
                                </span>
                                {isLatest && (
                                  <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs font-semibold rounded-full">
                                    Latest
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Version Details */}
                            <p className="text-xs text-gray-500 mb-2">
                              {formatDate(version.createdAt)}
                            </p>

                            {/* Content Preview */}
                            <p className="text-xs text-gray-600 line-clamp-3 mb-3">
                              {version.content.substring(0, 100)}...
                            </p>

                            {/* Restore Button */}
                            {!isLatest && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setRestoreModal({ isOpen: true, version })}
                                className="w-full"
                              >
                                Restore
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Restore Confirmation Modal */}
      <Modal
        isOpen={restoreModal.isOpen}
        onClose={() => setRestoreModal({ isOpen: false, version: null })}
        title="Restore Version"
      >
        {restoreModal.version && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                This will create a <strong>new version</strong> with the content from
                version {restoreModal.version.version}. The current version and all
                history will be preserved.
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Version Details:</p>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p>
                  <strong>Version:</strong> {restoreModal.version.version}
                </p>
                <p>
                  <strong>Created:</strong> {formatDate(restoreModal.version.createdAt)}
                </p>
                <p className="mt-2">
                  <strong>Content Preview:</strong>
                </p>
                <p className="text-gray-600 text-xs mt-1 line-clamp-3">
                  {restoreModal.version.content.substring(0, 150)}...
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setRestoreModal({ isOpen: false, version: null })}
              >
                Cancel
              </Button>
              <Button onClick={handleRestore}>Restore Version</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
